import { ApiError } from '../api/client';

/**
 * Staff sign-in failure copy (Task: helpful lock-screen messages).
 *
 * `POST /site-users/verify-login` already returns DISTINCT error details for
 * the failure modes a locked-out user cares about (no account, wrong code,
 * expired invitation, rate-limited, suspended). This module classifies the
 * thrown ApiError and pairs the explanation with a concrete recovery path so
 * the lock screens are never a dead end.
 */

export type StaffLoginFailureKind =
    | 'no_account'
    | 'wrong_code'
    | 'code_required'
    | 'expired_invitation'
    | 'suspended'
    | 'rate_limited'
    | 'network'
    | 'other';

export interface StaffLoginFailure {
    kind: StaffLoginFailureKind;
    /** What went wrong, in plain language. */
    message: string;
    /** What to do next — who to contact / what to check. */
    hint: string;
}

/**
 * Audience-specific copy: who a locked-out user should contact and how the
 * invitation code is framed. Staff are pointed at the Synterra Health team
 * (and warned off the internal admin code); site workers are pointed at
 * their site administrator.
 */
interface LoginFailureAudience {
    /** Who to contact for access / re-invitations. */
    contact: string;
    /** Appended to the no-account hint. */
    contactHint: string;
    /** Extra framing for the wrong-code hint. */
    wrongCodeQualifier: string;
}

const STAFF_AUDIENCE: LoginFailureAudience = {
    contact: 'the Synterra Health team',
    contactHint:
        'If you believe you should have access, contact the Synterra Health team ' +
        'to be invited. Note: staff sign-in (email + invitation code) is a ' +
        'separate system from the internal admin code.',
    wrongCodeQualifier: ' (it is not the admin code or the research-preview code)',
};

const SITE_AUDIENCE: LoginFailureAudience = {
    contact: 'your site administrator',
    contactHint:
        'If you believe you should have access, contact your site administrator ' +
        'to be invited.',
    wrongCodeQualifier: '',
};

export function describeStaffLoginFailure(err: unknown): StaffLoginFailure {
    return classifyLoginFailure(err, STAFF_AUDIENCE);
}

/**
 * Site-worker-flavored variant for the Site Portal sign-in screen: the same
 * classification of `POST /site-users/verify-login` failures, but recovery
 * paths point at the site administrator instead of the staff/admin framing.
 */
export function describeSiteLoginFailure(err: unknown): StaffLoginFailure {
    return classifyLoginFailure(err, SITE_AUDIENCE);
}

function classifyLoginFailure(err: unknown, audience: LoginFailureAudience): StaffLoginFailure {
    if (!(err instanceof ApiError)) {
        return {
            kind: 'other',
            message: 'Something went wrong while signing in.',
            hint: `Please try again. If it keeps failing, contact ${audience.contact}.`,
        };
    }
    if (err.isNetwork || err.kind === 'server') {
        return {
            kind: 'network',
            message: 'Could not reach the server to sign you in.',
            hint: 'Check your connection and try again in a moment.',
        };
    }
    if (err.kind === 'rate_limited' || err.status === 429) {
        return {
            kind: 'rate_limited',
            message: 'Too many sign-in attempts for this account.',
            hint: `Wait about 15 minutes and try again. If you no longer have your invitation code, ask ${audience.contact} to re-issue it.`,
        };
    }

    const detail = (err.detail || '').toLowerCase();
    if (detail.includes('no account found')) {
        return {
            kind: 'no_account',
            message: 'No account was found for this email address.',
            hint:
                'Double-check the spelling and use the exact email your invitation was sent to. ' +
                audience.contactHint,
        };
    }
    if (detail.includes('suspended')) {
        return {
            kind: 'suspended',
            message: 'This account has been suspended.',
            hint: `Contact ${audience.contact} to have it reinstated.`,
        };
    }
    if (detail.includes('expired')) {
        return {
            kind: 'expired_invitation',
            message: 'Your invitation has expired.',
            hint: `Contact ${audience.contact} to request a fresh invitation — codes are only valid for a limited time.`,
        };
    }
    if (detail.includes('invitation code is required')) {
        return {
            kind: 'code_required',
            message: 'An invitation code is required the first time you sign in.',
            hint: 'Enter the one-time code from your invitation email. After the first sign-in, email alone is enough.',
        };
    }
    if (detail.includes('invalid invitation code')) {
        return {
            kind: 'wrong_code',
            message: 'That invitation code does not match this account.',
            hint:
                `Copy the code exactly as it appears in your invitation email${audience.wrongCodeQualifier}. ` +
                `If it still fails, ask ${audience.contact} to re-send your invitation.`,
        };
    }

    return {
        kind: 'other',
        message: err.detail || 'Sign-in failed.',
        hint: audience.contactHint,
    };
}
